<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

// Sell drawer title bar (Figma 34796:138632): title left, round close right;
// a round back button joins the title on the steps after the first.

interface Props {
  title: string
  canGoBack: boolean
  /** Blocks back / close while the listing is being created. */
  disabled: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'close'): void
}>()
</script>

<template>
  <Box align="center" justify="between" gap="2" block class="sell-header">
    <Box align="center" gap="3">
      <Box
        v-if="canGoBack"
        is="button"
        type="button"
        align="center"
        justify="center"
        class="sell-header__button"
        aria-label="Back"
        :disabled="disabled"
        @click="emit('back')"
      >
        <PiIcon name="arrow-left" class="sell-header__icon" />
      </Box>
      <Text type="title-lg" weight="semibold" is="h2" style="line-height: 30px">{{ title }}</Text>
    </Box>
    <Box
      is="button"
      type="button"
      align="center"
      justify="center"
      class="sell-header__button"
      aria-label="Close"
      :disabled="disabled"
      @click="emit('close')"
    >
      <PiIcon name="times" class="sell-header__icon" />
    </Box>
  </Box>
</template>

<style scoped>
/* The shared drawer header pads 20 / 24 / 16; Figma's bar is 70px tall with
   20px all round — pull the row out to match without touching the kit. */
.sell-header {
  margin: 0 -0.25rem 0.25rem;
  width: calc(100% + 0.5rem);
}

@media (max-width: 992px) {
  .sell-header {
    margin: 0 -0.5rem 0.25rem;
    width: calc(100% + 1rem);
  }
}

.sell-header__button {
  position: relative;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border: 1px solid var(--p-surface-200);
  border-radius: 999px;
  background: var(--p-surface-0);
  cursor: pointer;
  transition: background 0.15s ease;
}

/* 44px touch target around the 30px circle. */
.sell-header__button::before {
  content: '';
  position: absolute;
  inset: -7px;
}

.sell-header__button:hover:not(:disabled) {
  background: var(--p-surface-100);
}

.sell-header__button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sell-header__button:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.sell-header__icon {
  font-size: 11px;
  color: var(--p-text-color);
}
</style>
